
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Skeleton, Popover, Typography, Button, Grid, useMediaQuery, Box } from "@mui/material";
import AddCircleTwoToneIcon from "@mui/icons-material/AddCircleTwoTone";
import {
  setGuestCount,
  setSelectedEventOrStdResType,
  updateDisCountPrice,
  updateTotalPrice,
} from "../../store/slice/Ticketing.slice";
import GuestCount from "./GuestCount";
import { format_Number } from "../../utils/utils";
import { useTheme } from "@emotion/react";
import { UsePopoverPosition } from "../../hooks/UsePopoverPosition";

const EventOrStandardReservationType = ({
  Ref,
  isOpen,
  handleClose,
  eventStdTicketLoader,
}) => {
  const { css } = useSelector((store) => store?.widgetSettings);
  const Css = css?.find((item) => item?.cssTabFieldsGroupName === "Ticket type")?.fields;
  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;
  const theme = useTheme();

  // Breakpoints for responsive layout
  const isMobile = useMediaQuery("(max-width:600px)");   // xs
  const isTablet = useMediaQuery("(max-width:960px)");   // sm–md

  const {
    EventOrStdTicketTypesData,
    SelectedEventOrStdResType,
    adult,
    totalCount,
    reservType,
    TotalPrice,
    disCountPrice,
    languageWords,
    guestCounts,
  } = useSelector((store) => store?.Ticketing);

  const dispatch = useDispatch();

  const {
    anchorOriginVert,
    transformOriginVert,
    paperSx,
    mobileOverrideProps,
  } = UsePopoverPosition({ current: Ref }, isOpen, 400);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelect = (item) => {
    localStorage?.setItem("idreservation", item?.idreservationtype);
    dispatch(setSelectedEventOrStdResType(item));
    if (item?.idreservationtype !== Object.keys(guestCounts)?.[0]) {
      dispatch(setGuestCount({ idreservationtype: item.idreservationtype, guestType: "adult", count: 1 }));
    }
  };

  const calculateTicketingDiscount = () => {
    const discountConfig = {
      hasDiscountPerTicket1: EventOrStdTicketTypesData?.hasDiscountPerTicket1,
      hasDiscountPerTicket2: EventOrStdTicketTypesData?.hasDiscountPerTicket2,
      discountPercentagePerTicket1: EventOrStdTicketTypesData?.discountPercentagePerTicket1,
      discountPercentagePerTicket2: EventOrStdTicketTypesData?.discountPercentagePerTicket2,
      numberOfTicketsEligibleForDiscount1: EventOrStdTicketTypesData?.numberOfTicketsEligibleForDiscount1,
      numberOfTicketsEligibleForDiscount2: EventOrStdTicketTypesData?.numberOfTicketsEligibleForDiscount2,
    };
    let eligibleDiscounts = [];
    if (discountConfig.hasDiscountPerTicket1 && totalCount >= discountConfig.numberOfTicketsEligibleForDiscount1)
      eligibleDiscounts.push({ percentage: discountConfig.discountPercentagePerTicket1 });
    if (discountConfig.hasDiscountPerTicket2 && totalCount >= discountConfig.numberOfTicketsEligibleForDiscount2)
      eligibleDiscounts.push({ percentage: discountConfig.discountPercentagePerTicket2 });
    let applicableDiscount = 0;
    if (eligibleDiscounts.length > 0) {
      eligibleDiscounts.sort((a, b) => b.percentage - a.percentage);
      applicableDiscount = eligibleDiscounts[0].percentage;
    }
    const discountAmount = TotalPrice * (applicableDiscount / 100);
    dispatch(updateDisCountPrice(parseFloat(TotalPrice - discountAmount).toFixed(2)));
  };

  const calculateTotalPrice = () => {
    const guestCount = guestCounts[SelectedEventOrStdResType?.idreservationtype] || {};
    const { adult = 1, child = 0, kid = 0, baby = 0, toddler = 0 } = guestCount;
    let totalPrice = 0;
    if (reservType?.isStandardTicket && Object.keys(guestCount)?.length > 0) {
      totalPrice =
        adult   * SelectedEventOrStdResType?.dayProductPrice1 +
        child   * SelectedEventOrStdResType?.dayProductPrice2 +
        kid     * SelectedEventOrStdResType?.dayProductPrice3 +
        baby    * SelectedEventOrStdResType?.dayProductPrice4 +
        toddler * SelectedEventOrStdResType?.dayProductPrice5;
    } else {
      totalPrice = adult * SelectedEventOrStdResType?.eventProductPrice;
    }
    dispatch(updateTotalPrice(totalPrice));
  };

  useEffect(() => {
    if (SelectedEventOrStdResType?.idreservationtype) {
      calculateTotalPrice();
      calculateTicketingDiscount();
    }
  }, [totalCount, SelectedEventOrStdResType, guestCounts]);

  // ── Price block (reused in both layouts) ─────────────────────────────────
  const PriceBlock = ({ isActive, isDiscountApplicable, normalPrice, fontSize = "14px" }) => {
    if (isDiscountApplicable && TotalPrice > 0 && disCountPrice > 0) {
      return (
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography
            component="span"
            sx={{
              textDecoration: "line-through",
              fontSize: "12px",
              mr: 0.5,
              color: isActive ? Css?.ticketTypePriceHighlightClr : Css?.ticketTypePriceClr,
            }}
          >
            €{format_Number(parseFloat(TotalPrice).toFixed(2))}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize,
              fontWeight: 700,
              color: isActive ? Css?.ticketTypePriceHighlightClr : Css?.ticketTypePriceClr,
            }}
          >
            €{format_Number(parseFloat(disCountPrice).toFixed(2))}
          </Typography>
        </Box>
      );
    }
    if (normalPrice > 0) {
      return (
        <Typography
          sx={{
            fontSize,
            fontWeight: 700,
            flexShrink: 0,
            color: isActive ? Css?.ticketTypePriceHighlightClr : Css?.ticketTypePriceClr,
          }}
        >
          €{format_Number(parseFloat(normalPrice).toFixed(2))}
        </Typography>
      );
    }
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Popover
      open={isOpen}
      anchorEl={Ref}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      {...mobileOverrideProps}
      PaperProps={{
        sx: {
          ...paperSx,
          // Override width/maxWidth for this component — needs more room than
          // the reservation type popovers because of the GuestCount + button column
          width: isMobile ? "calc(100vw - 16px)" : isTablet ? "95%" : "900px",
          maxWidth: "960px",
          backgroundColor: theme?.widget?.EventRestypeBgClr || "#fff",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme?.widget?.EventRestypeBgClr || "#fff",
          width: "100%",
          p: { xs: 1.5, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: "0.75rem", sm: "1rem" },
        }}
      >
        {/* ── Skeleton loader ── */}
        {eventStdTicketLoader
          ? Array.from({ length: 3 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                  width: "100%",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: { xs: "100%", sm: 140 },
                    height: { xs: 140, sm: 110 },
                    borderRadius: "10px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="text" width="60%" height={22} />
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton variant="rectangular" width="35%" height={34} sx={{ borderRadius: "7px", mt: 1 }} />
                </Box>
              </Box>
            ))
          : EventOrStdTicketTypesData?.ticketTypeForReservations?.map((item) => {
              const isActive = SelectedEventOrStdResType?.idreservationtype === item?.idreservationtype;
              const isDiscountApplicable =
                isActive &&
                ((EventOrStdTicketTypesData?.hasDiscountPerTicket1 &&
                  totalCount >= EventOrStdTicketTypesData?.numberOfTicketsEligibleForDiscount1) ||
                  (EventOrStdTicketTypesData?.hasDiscountPerTicket2 &&
                    totalCount >= EventOrStdTicketTypesData?.numberOfTicketsEligibleForDiscount2));
              const normalPrice = isActive && TotalPrice > 0 ? TotalPrice : item?.dayProductPrice1 * adult;
              const hasImage = Boolean(item?.imageFileName);

              return (
                <Box
                  key={item?.idreservationtype}
                  sx={{
                    width: "100%",
                    borderRadius: "10px",
                    border: `1px solid ${Css?.ticketTypeItemBorderClr}`,
                    backgroundColor: isActive ? Css?.ticketTypeHighlightItemBgClr : Css?.tickettypeItemBgClr,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
                    cursor:"pointer"
                  }}
                  onClick={(e) => {e.stopPropagation(); handleSelect(item); handleClose(); }}
                >
                  {isMobile ? (
                    // ════ MOBILE layout ════
                    <Box sx={{ display: "flex", flexDirection: "column" }}>

                      {/* Image — full width, fixed height */}
                      {hasImage && (
                        <Box sx={{ width: "100%", height: 160, overflow: "hidden", flexShrink: 0 }}>
                          <Box
                            component="img"
                            src={`${IMG_URL}/upload/ReservationType/${item?.imageFileName}`}
                            alt={item?.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        </Box>
                      )}

                      {/* Content */}
                      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                        {/* Title + price on same row */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                          <Typography
                            fontWeight={700}
                            sx={{
                              fontSize: "14px",
                              flex: "1 1 0",
                              minWidth: 0,
                              wordBreak: "break-word",
                              color: isActive ? Css?.ticketTypeHeadingHighlightClr : Css?.ticketTypeHeadingClr,
                            }}
                          >
                            {item?.name}
                          </Typography>
                          <PriceBlock
                            isActive={isActive}
                            isDiscountApplicable={isDiscountApplicable}
                            normalPrice={normalPrice}
                            fontSize="13px"
                          />
                        </Box>

                        {item?.info && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              lineHeight: 1.5,
                              color: isActive ? Css?.ticketTypeInfoHighlightClr : Css?.ticketTypeInfoClr,
                            }}
                          >
                            {item?.info}
                          </Typography>
                        )}

                        <GuestCount item={item} handleSelect={handleSelect} />

                 

                        {EventOrStdTicketTypesData?.showMessageWhenTicketsLessFlag &&
                          isActive &&
                          SelectedEventOrStdResType?.maximumTickets - SelectedEventOrStdResType?.soldTickets <
                            EventOrStdTicketTypesData?.showMessageWhenTicketsLessThan && (
                            <Typography fontSize="12px" color="warning.main">
                              Only {SelectedEventOrStdResType?.maximumTickets - SelectedEventOrStdResType?.soldTickets} tickets available
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  ) : (
                    // ════ DESKTOP / TABLET layout ════
                    // Grid: [image 2.5] [info 5.5] [guest+btn 4] = 12
                    <Grid container sx={{ minHeight: 160 }}>

                      {/* Image column */}
                      {hasImage && (
                        <Grid item sm={3} md={2.5}
                          sx={{
                            // Image fills the entire card height
                            "& > div": { height: "100%" },
                          }}
                        >
                          <Box sx={{ overflow: "hidden", height: "100%", minHeight: 160 }}>
                            <Box
                              component="img"
                              src={`${IMG_URL}/upload/ReservationType/${item?.imageFileName}`}
                              alt={item?.name}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </Box>
                        </Grid>
                      )}

                      {/* Info column */}
                      <Grid
                        item
                        sm={hasImage ? 5 : 8}
                        md={hasImage ? 5.5 : 8}
                        sx={{ p: 2, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.75 }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                          <Typography
                            fontWeight={700}
                            sx={{
                              fontSize: { sm: "14px", md: "15px" },
                              flex: "1 1 0",
                              minWidth: 0,
                              wordBreak: "break-word",
                              color: isActive ? Css?.ticketTypeHeadingHighlightClr : Css?.ticketTypeHeadingClr,
                            }}
                          >
                            {item?.name}
                          </Typography>
                          <PriceBlock
                            isActive={isActive}
                            isDiscountApplicable={isDiscountApplicable}
                            normalPrice={normalPrice}
                            fontSize="14px"
                          />
                        </Box>

                        {item?.info && (
                          <Typography
                            sx={{
                              fontSize: { sm: "12px", md: "13px" },
                              lineHeight: 1.6,
                              color: isActive ? Css?.ticketTypeInfoHighlightClr : Css?.ticketTypeInfoClr,
                            }}
                          >
                            {item?.info}
                          </Typography>
                        )}
                      </Grid>

                      {/* Guest count + button column */}
                      <Grid
                        item
                        sm={hasImage ? 4 : 4}
                        md={hasImage ? 4 : 4}
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 1.5,
                     //     borderLeft: `1px solid ${Css?.ticketTypeItemBorderClr || "#eee"}`,
                        }}
                      >
                        <GuestCount item={item} handleSelect={handleSelect} />
{/* 
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AddCircleTwoToneIcon />}
                          sx={{
                            borderRadius: "8px",
                            border: `1px solid ${Css?.ticketTypAddBtnBorderClr}`,
                            backgroundColor: "#fff",
                            color: isActive ? Css?.ticketTypeHighlightAddBtnTxtClr : Css?.ticketTypeAddBtnTxtClr,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => { handleSelect(item); handleClose(); }}
                        >
                          {languageWords?.Add}
                        </Button> */}

                        {EventOrStdTicketTypesData?.showMessageWhenTicketsLessFlag &&
                          isActive &&
                          SelectedEventOrStdResType?.maximumTickets - SelectedEventOrStdResType?.soldTickets <
                            EventOrStdTicketTypesData?.showMessageWhenTicketsLessThan && (
                            <Typography fontSize="12px" color="warning.main" textAlign="center">
                              Only {SelectedEventOrStdResType?.maximumTickets - SelectedEventOrStdResType?.soldTickets} tickets available
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  )}
                </Box>
              );
            })}
      </Box>
    </Popover>
  );
};

export default EventOrStandardReservationType;



